import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize, forkJoin, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { SortEvent } from 'primeng/api';
import { DynamicFilterConfig, DynamicFilterValues, createDynamicFilterState } from '@card-software/library';
import { AssetProxyService } from '../../proxies/asset/asset.proxy.service';
import { CategoryProxyService } from '../../proxies/asset/category.proxy.service';
import { TypeProxyService } from '../../proxies/asset/types.proxy.service';
import { Asset } from '../../models/Asset';
import { AssetCategory } from '../../models/AssetCategory';
import { AssetType } from '../../models/AssetType';
import { AssetSearchCommand } from '../../models/commands/AssetSearchCommand';

@Component({
  selector: 'app-assets',
  templateUrl: './assets.component.html',
  styleUrl: './assets.component.scss',
})
export class AssetsComponent implements OnInit, OnDestroy {
  private readonly assetProxyService = inject(AssetProxyService);
  private readonly categoryProxyService = inject(CategoryProxyService);
  private readonly typeProxyService = inject(TypeProxyService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  isLoading = signal(0);
  assets = signal<Asset[]>([]);
  currentPage = signal(1);
  pageSize = signal(25);
  totalRecords = signal(0);
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  showFilters = signal(true);

  categories = signal<AssetCategory[]>([]);
  assetTypes = signal<AssetType[]>([]);

  readonly filterStateConfig = computed(() => (this.categories().length > 0 ? this.filterConfig() : null));
  readonly filterState = createDynamicFilterState({ storageKey: 'asset-filters', config: this.filterStateConfig });

  private destroy$ = new Subject<void>();
  private lastSearchCriteria: AssetSearchCommand = {};

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        if (this.translate.currentLang && this.categories().length > 0) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.loadFilterData();

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfig();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadFilterData(): void {
    forkJoin({
      categories: this.categoryProxyService.getCategories(),
      types: this.typeProxyService.getTypes(),
    }).subscribe({
      next: ({ categories, types }) => {
        this.categories.set(categories.data ?? []);
        this.assetTypes.set(types.data ?? []);
      },
      error: (e) => {
        console.error('Error loading filter data:', e);
      },
    });
  }

  private tryInitializeFilterConfig(): void {
    if (this.translate.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translate.instant(key);
      return translation === key ? fallback : translation;
    };

    const categoryOptions = this.categories().map((c) => ({
      label: c.name,
      value: c.id,
    }));

    const typeOptions = this.assetTypes().map((t) => ({
      label: t.name,
      value: t.id,
    }));

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'assetId',
          label: translate('Assets_Filters.AssetId', 'Asset ID'),
          placeholder: translate('Assets_Filters.EnterAssetId', 'Enter asset ID'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'assetCategoryReference',
          label: translate('Assets_Filters.Category', 'Category'),
          placeholder: translate('Assets_Filters.SelectCategory', 'Select category'),
          options: categoryOptions,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'assetTypeReference',
          label: translate('Assets_Filters.Type', 'Type'),
          placeholder: translate('Assets_Filters.SelectType', 'Select type'),
          options: typeOptions,
          showClear: true,
          defaultVisible: true,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private onSearch(searchCriteria: AssetSearchCommand): void {
    this.lastSearchCriteria = searchCriteria;
    this.startLoading();
    this.assetProxyService
      .searchAssets(searchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.assets.set(response?.data?.items || []);
          this.totalRecords.set(response?.data?.totalCount || 0);
        },
        error: (error) => {
          console.error('Error searching assets:', error);
          this.assets.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const searchCriteria: AssetSearchCommand = {
      assetId: (filters['assetId'] as string | undefined)?.trim() || undefined,
      assetCategoryReference: (filters['assetCategoryReference'] as string | undefined) || undefined,
      assetTypeReference: (filters['assetTypeReference'] as string | undefined) || undefined,
    };

    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page || 0) + 1);
    this.pageSize.set(event.rows || this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = event.field;
    const newOrder = event.order ?? -1;

    if (newField === this.sortField() && newOrder === this.sortOrder()) {
      return;
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  getRowData(rowData: unknown): Asset {
    return rowData as Asset;
  }

  redirectToAssetDetails(assetId?: string): void {
    this.router.navigate(['/asset-details'], { queryParams: { assetId } });
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }
}
